import { isPlainObject } from '@art-suite/art-core-ts';
import { createContext, type ReactNode, useContext, useEffect, useReducer, useRef, useState } from 'react';

type Subscriber<T> = (value: T) => void;

type WrapperApi<T> = {
  getLatest: () => T | undefined;
  subscribe: (cb: Subscriber<T>) => () => void;
  notify: (value: T) => void;
};

const shallowEqual = <T extends object>(a: T, b: T): boolean => {
  for (const key in a) {
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
};

// Equality used by useSelector. If both sides are plain objects, do per-key
// Object.is. Otherwise (primitives, arrays, class instances, functions, null,
// mismatched shapes), compare as a single unit via Object.is. This lets
// selectors return arbitrary shapes — the caller stabilizes identity by
// returning the prev sub-reference when contents haven't changed.
const isEqual = <R,>(a: R, b: R): boolean => {
  if (Object.is(a, b)) return true;
  if (isPlainObject(a) && isPlainObject(b)) return shallowEqual(a, b);
  return false;
};

const selectKeys = <T extends object, K extends keyof T>(keys: readonly K[], source: T): Pick<T, K> => {
  const out: Record<string, unknown> = {};
  for (const key of keys) out[key as string] = source[key];
  return out as Pick<T, K>;
};

/**
 * Creates an isolated context-subscriber instance: a Provider that registers
 * subscribers outside React's render cycle, a notify hook for the host context
 * to push new values, and a useSlice hook that re-renders consumers if and only
 * if their declared fields are Object.is-different from the slice they are
 * currently rendering.
 *
 * Subscriber callbacks are invoked via queueMicrotask so notify() can be called
 * during the host's render without triggering "Cannot update a component while
 * rendering a different component" warnings.
 */
export function createContextSubscribers<T extends object>(name: string) {
  const WrapperContext = createContext<WrapperApi<T> | null>(null);

  function Provider({ children }: { children: ReactNode }) {
    const [api] = useState<WrapperApi<T>>(() => {
      const latestRef: { current: T | undefined } = { current: undefined };
      const subscribers = new Set<Subscriber<T>>();
      let flushScheduled = false;
      return {
        getLatest: () => latestRef.current,
        subscribe: cb => {
          subscribers.add(cb);
          return () => {
            subscribers.delete(cb);
          };
        },
        notify: (value: T) => {
          latestRef.current = value;
          if (flushScheduled) return;
          flushScheduled = true;
          queueMicrotask(() => {
            flushScheduled = false;
            const current = latestRef.current as T;
            subscribers.forEach(cb => cb(current));
          });
        },
      };
    });
    return <WrapperContext.Provider value={api}>{children}</WrapperContext.Provider>;
  }

  function useApi(): WrapperApi<T> {
    const api = useContext(WrapperContext);
    if (!api) throw new Error('createContextSubscribers: missing Provider in tree');
    return api;
  }

  function useNotifySubscribers() {
    return useApi().notify;
  }

  /**
   * Subscribe to a fixed set of fields from the source value. Returns a
   * Pick<T, K> that re-renders only when one of the picked fields is
   * Object.is-different from the slice currently rendered.
   */
  function useSlice<K extends keyof T>(fields: readonly K[]): Pick<T, K> {
    const api = useApi();

    const computeSlice = (): Pick<T, K> => {
      const latest = api.getLatest();
      if (latest === undefined) {
        throw new Error(`${name}.useSlice: no latest value. Be sure to call useNotifySubscribers() from your ContextProvider before using useSlice().`);
      }
      return selectKeys(fields, latest);
    };

    const [slice, setSlice] = useState<Pick<T, K>>(computeSlice);

    useEffect(
      () =>
        api.subscribe(() => {
          setSlice(current => {
            const next = computeSlice();
            return shallowEqual(next, current) ? current : next;
          });
        }),
      [api],
    );

    return slice;
  }

  /**
   * Subscribe to a derived shape via a selector. The selector receives the
   * source value and the PREVIOUS selector result, so it can return the prev
   * sub-reference when contents haven't changed (stabilizes downstream
   * identity). Pass `deps` to re-evaluate during render when external state
   * the selector closes over changes — same semantics as React's hook deps.
   */
  function useSelector<R>(
    selector: (value: T, prev: R | undefined) => R,
    deps?: readonly unknown[],
  ): R {
    const api = useApi();
    const selectorRef = useRef(selector);
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    selectorRef.current = selector;

    const computeSlice = (prev: R | undefined): R => {
      const latest = api.getLatest();
      if (latest === undefined) {
        throw new Error(`${name}.useSelector: no latest value. Be sure to call useNotifySubscribers() from your ContextProvider before using useSelector().`);
      }
      return selectorRef.current(latest, prev);
    };

    const sliceRef = useRef<R>(computeSlice(undefined));
    sliceRef.current ??= computeSlice(sliceRef.current);

    const updateSlice = () => {
      const newSlice = computeSlice(sliceRef.current);
      if (!isEqual(newSlice, sliceRef.current)) {
        sliceRef.current = newSlice;
        return true;
      }
      return false;
    };

    // deps changed, update slice
    const depsRef = useRef(deps);
    if (deps) {
      let changed = false;
      if (!depsRef.current) changed = true;
      else {
        for (let i = 0; i < deps.length; i++) {
          if (!Object.is(depsRef.current[i], deps[i])) {
            changed = true;
            break;
          }
        }
      }
      if (changed) {
        depsRef.current = deps;
        updateSlice();
      }
    }

    // subscribe to updates from teh context
    useEffect(
      () =>
        api.subscribe(() => {
          if (updateSlice()) {
            forceUpdate();
          }
        }),
      [api],
    );

    return sliceRef.current;
  }

  return { Provider, useNotifySubscribers, useSlice, useSelector };
}
